import { convertHTMLToMarkdown } from '@/shared';
import { open, save } from 'tauri/api/dialog';
import { readTextFile, writeFile } from 'tauri/api/fs';

abstract class IFileHandler {
  abstract openFileDialog(): Promise<string>;
  abstract exportFileDialog(type: ExportType, html: string): Promise<void>;
  abstract saveFile(html: string): Promise<void>;
}
class IFileHandlerHelper {
  getContentFromType(type: ExportType, html: string) {
    switch (type) {
      case 'html':
        return html;
      case 'md':
        return convertHTMLToMarkdown(html);
      case 'pdf':
        throw new Error('Not Implemented - PDF');
    }
  }

  getTitleFromHtml(html: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('h1')?.innerHTML || 'Untitled';
  }
}

export type ExportType = 'md' | 'pdf' | 'html';

class TauriFileHandler extends IFileHandlerHelper implements IFileHandler {
  saveFilepath: string | undefined;

  async saveFile(html: string) {
    if (!this.saveFilepath) {
      this.saveFilepath = (await save({ filter: 'md' })) as string;
    }
    const text = this.getContentFromType('md', html);
    await writeFile({ path: this.saveFilepath, contents: text });
  }

  async openFileDialog() {
    try {
      const filePath = (await open({ multiple: false, filter: 'md' })) as string;
      const fileText = await readTextFile(filePath);
      return fileText;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async exportFileDialog(type: ExportType, html: string) {
    try {
      const filePath = (await save({ filter: type })) as string;
      const text = this.getContentFromType(type, html);
      await writeFile({ path: filePath, contents: text });
    } catch (error) {
      console.log(error);
    }
  }
}

class BrowserFileHandler extends IFileHandlerHelper implements IFileHandler {
  fileHandle: any | undefined;

  async saveFile(html: string) {
    if (!this.fileHandle) {
      this.fileHandle = await this.getNewFileHandle();
    }
    const writable = await this.fileHandle.createWritable();
    const text = this.getContentFromType('md', html);
    await writable.write(text);
    await writable.close();
  }

  async getNewFileHandle() {
    const options = {
      types: [
        {
          description: 'Markdown',
          accept: {
            'text/markdown': ['.md'],
          },
        },
      ],
      excludeAcceptAllOption: true,
    };
    const handle = await (window as any).showSaveFilePicker(options);
    return handle;
  }

  async openFileDialog() {
    return new Promise<string>((resolve, reject) => {
      try {
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        fileInput.click();
        if (!fileInput) throw new Error('#file-input not found');
        fileInput.onchange = async () => {
          const reader = new FileReader();
          reader.onload = function() {
            const text = reader.result as string;
            resolve(text);
          };
          if (!fileInput?.files?.[0]) throw Error('No file chosen');
          reader.readAsText(fileInput.files[0]);
        };
        return '' as any;
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  }

  async exportFileDialog(type: ExportType, html: string) {
    try {
      const text = this.getContentFromType(type, html);
      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
      element.setAttribute('download', `${this.getTitleFromHtml(html)}.${type}`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.log(error);
    }
  }
}

const isTauri = !!(window as any).__TAURI_INVOKE_HANDLER__;
export const FileHandler = isTauri ? new TauriFileHandler() : new BrowserFileHandler();
